import React from "react";
import { Link, Navigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import Nav, { whatsappOrderUrl } from "../components/Nav";
import Footer from "../components/Footer";
import Seo from "../components/Seo";
import { findService, SERVICES } from "../lib/services";

const SITE = "https://riznwash.com";
const EASE = [0.22, 1, 0.36, 1];

export default function ServiceLanding() {
  const { serviceSlug } = useParams();
  const svc = findService(serviceSlug);
  if (!svc) return <Navigate to="/" replace />;

  const path = `/services/${svc.slug}`;
  const jsonLd = [
    {
      "@context": "https://schema.org",
      "@type": "Service",
      serviceType: svc.name,
      name: svc.title,
      description: svc.metaDescription,
      provider: { "@type": "LocalBusiness", name: "RIZ N WASH", areaServed: "Gurugram" },
      areaServed: { "@type": "City", name: "Gurugram" },
      offers: {
        "@type": "Offer",
        priceCurrency: "INR",
        price: String(svc.priceFrom),
        priceSpecification: {
          "@type": "UnitPriceSpecification",
          priceCurrency: "INR",
          price: svc.priceFrom,
          unitText: svc.priceUnit,
        },
        availability: "https://schema.org/InStock",
      },
    },
    {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        { "@type": "ListItem", position: 1, name: "RIZ N WASH", item: SITE },
        { "@type": "ListItem", position: 2, name: "Services", item: `${SITE}/services` },
        { "@type": "ListItem", position: 3, name: svc.name, item: `${SITE}${path}` },
      ],
    },
    {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      mainEntity: svc.faqs.map((f) => ({
        "@type": "Question",
        name: f.q,
        acceptedAnswer: { "@type": "Answer", text: f.a },
      })),
    },
  ];

  return (
    <div className="min-h-screen bg-rw-bg text-rw-ink" data-testid={`service-page-${svc.slug}`}>
      <Seo title={svc.title} description={svc.metaDescription} path={path} jsonLd={jsonLd} image={svc.hero} />
      <Nav />

      <section className="max-w-7xl mx-auto px-6 md:px-10 pt-10 pb-8 md:pt-24 md:pb-16 grid grid-cols-1 md:grid-cols-12 gap-8 items-end">
        <div className="md:col-span-7">
          <motion.nav
            initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, ease: EASE }}
            className="overline mb-4 flex flex-wrap items-center gap-2 text-rw-ink3"
            aria-label="Breadcrumb"
          >
            <Link to="/" className="hover:text-rw-ink">Home</Link>
            <span aria-hidden>·</span>
            <Link to="/services" className="hover:text-rw-ink">Services</Link>
            <span aria-hidden>·</span>
            <span className="text-rw-ink2">{svc.name}</span>
          </motion.nav>
          <motion.h1
            initial={{ opacity: 0, y: 22 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
            className="font-display font-light tracking-tight text-rw-ink leading-[0.98] text-4xl sm:text-5xl lg:text-[5.5rem]"
            data-testid="service-heading"
          >
            {svc.h1}
            <br />
            <em className="italic font-normal text-rw-blue">{svc.h1Accent}</em>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.3 }}
            className="mt-6 md:mt-8 text-base md:text-xl text-rw-ink2 max-w-xl leading-relaxed"
          >
            {svc.lede}
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.5 }}
            className="mt-8 flex flex-wrap gap-3 items-center"
          >
            <a
              href={whatsappOrderUrl(`Hi RIZ N WASH! I'd like to book ${svc.name}.`)}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-dark rounded-full px-6 py-3 text-sm md:text-base font-semibold"
              data-testid="service-book-btn"
            >
              Order on WhatsApp · from ₹{svc.priceFrom}/{svc.priceUnit.replace("per ", "")} →
            </a>
            <a href="tel:+917303241330" className="rounded-full px-5 py-3 text-sm font-semibold border border-rw-ink/20 hover:bg-rw-ink hover:text-white transition-colors">
              Call +91 73032 41330
            </a>
          </motion.div>
        </div>
        <div className="md:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }} animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1, ease: EASE, delay: 0.2 }}
            className="relative rounded-2xl overflow-hidden border border-rw-ink/10 aspect-[4/5] md:aspect-[3/4]"
          >
            <img src={svc.hero} alt={svc.name} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute bottom-4 left-4 right-4 bg-rw-bg2/90 backdrop-blur rounded-xl p-4">
              <div className="overline mb-1">Starts at</div>
              <div className="font-display text-2xl">₹{svc.priceFrom}<span className="text-base text-rw-ink2"> · {svc.priceUnit}</span></div>
              <div className="text-xs text-rw-ink2 mt-1">Turnaround {svc.turnaround}</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="bg-rw-bg2 border-y border-rw-ink/10">
        <div className="max-w-6xl mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-3">Our process</div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05] max-w-3xl">
            Every {svc.name.toLowerCase()} order goes through the same care.
          </h2>
          <div className="grid md:grid-cols-3 gap-6 mt-12">
            {svc.benefits.map((b, i) => (
              <div key={i} className="rounded-2xl border border-rw-ink/10 bg-rw-bg p-6" data-testid={`service-benefit-${i}`}>
                <div className="font-display text-4xl text-rw-blue">{String(i + 1).padStart(2, "0")}</div>
                <h3 className="font-display text-xl mt-3">{b.title}</h3>
                <p className="text-rw-ink2 text-sm mt-2">{b.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Ideal for */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <div className="overline mb-3">Ideal for</div>
        <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
          What we clean best.
        </h2>
        <ul className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 mt-10 text-rw-ink2">
          {svc.idealFor.map((it, i) => (
            <li key={i} className="py-3 border-b border-rw-ink/10 flex items-baseline gap-3">
              <span className="text-rw-blue">·</span>
              <span>{it}</span>
            </li>
          ))}
        </ul>
      </section>

      {/* FAQ */}
      <section className="bg-rw-bg2 border-y border-rw-ink/10">
        <div className="max-w-4xl mx-auto px-6 md:px-10 py-20">
          <div className="overline mb-3">Common questions</div>
          <h2 className="font-display text-3xl md:text-5xl font-light tracking-tight leading-[1.05]">
            {svc.name} · FAQ
          </h2>
          <dl className="mt-10 divide-y divide-rw-ink/10">
            {svc.faqs.map((f, i) => (
              <div key={i} className="py-6" data-testid={`service-faq-${i}`}>
                <dt className="font-display text-lg md:text-xl text-rw-ink">{f.q}</dt>
                <dd className="mt-2 text-rw-ink2 leading-relaxed">{f.a}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      {/* Related services */}
      <section className="max-w-6xl mx-auto px-6 md:px-10 py-20">
        <div className="overline mb-3">Also popular</div>
        <h3 className="font-display text-2xl md:text-3xl mb-6">Other RIZ N WASH services</h3>
        <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
          {svc.relatedServices
            .map((s) => SERVICES.find((sv) => sv.slug === s))
            .filter(Boolean)
            .map((r) => (
              <Link
                key={r.slug}
                to={`/services/${r.slug}`}
                className="group block rounded-2xl border border-rw-ink/10 bg-rw-bg2 p-6 hover:border-rw-blue transition-all"
                data-testid={`related-service-${r.slug}`}
              >
                <div className="overline text-rw-ink3">From ₹{r.priceFrom}</div>
                <div className="font-display text-xl mt-1 group-hover:text-rw-blue transition-colors">{r.name}</div>
                <div className="mt-3 text-sm text-rw-ink2">{r.lede.slice(0, 100)}…</div>
              </Link>
            ))}
        </div>
      </section>

      <Footer />
    </div>
  );
}
